import { api, ApiError } from "../api.js";
import { el } from "./dom.js";
import {
  MAX_MIN_LOBBY_SECONDS,
  MAX_WARMUP_SECONDS,
  type AdminMapEntry,
  type AdminPlayerSummary,
  type AdminRandomMapEntry,
  type AppVersions,
} from "@webolo/shared";

// Admin panel — gated server-side to the "bolo" account. The client-side
// route also redirects non-admin sessions, but the server is the source of
// truth (every endpoint behind requireAdmin).
export function adminScreen(onExit: () => void): HTMLElement {
  const versionsHost   = el("div", { class: "admin-section-body" }, el("span", { class: "muted" }, "Loading…"));
  const mapsHost       = el("div", { class: "admin-section-body" }, el("span", { class: "muted" }, "Loading…"));
  const randomMapsHost = el("div", { class: "admin-section-body" }, el("span", { class: "muted" }, "Loading…"));
  const turboHost      = el("div", { class: "admin-section-body" }, el("span", { class: "muted" }, "Loading…"));
  const warmupHost     = el("div", { class: "admin-section-body" }, el("span", { class: "muted" }, "Loading…"));
  const minLobbyHost   = el("div", { class: "admin-section-body" }, el("span", { class: "muted" }, "Loading…"));
  const siegeHost      = el("div", { class: "admin-section-body" }, el("span", { class: "muted" }, "Loading…"));
  const siegeMapsHost  = el("div", { class: "admin-section-body" }, el("span", { class: "muted" }, "Loading…"));
  const brHost         = el("div", { class: "admin-section-body" }, el("span", { class: "muted" }, "Loading…"));
  const brMapsHost     = el("div", { class: "admin-section-body" }, el("span", { class: "muted" }, "Loading…"));
  const playerHost     = el("div", { class: "admin-section-body" });

  const wrap = el(
    "div",
    { class: "screen admin" },
    el(
      "div",
      { class: "admin-shell" },
      el(
        "header",
        { class: "admin-head" },
        el("div", { class: "admin-logo" },
          el("span", { class: "logo-we" }, "WE"),
          el("span", { class: "logo-bolo" }, "BOLO"),
          el("span", { class: "admin-tag" }, "ADMIN"),
        ),
        el("button", { class: "admin-back", type: "button", onclick: onExit }, "← BACK TO LOBBY"),
      ),
      el(
        "section",
        { class: "admin-section" },
        el("h2", { class: "admin-section-ttl" }, "PLAYER PANEL"),
        el("p", { class: "admin-section-desc" },
          "Search a player by username or email, then grant or revoke the one-time " +
          "Full Game Unlock. Premium is account-level, so it applies on every " +
          "platform (Apple / Steam / Android) the account signs into. Web is always " +
          "free regardless. Direct MySQL edits remain a fallback."),
        playerHost,
      ),
      el(
        "section",
        { class: "admin-section" },
        el("h2", { class: "admin-section-ttl" }, "VERSIONS"),
        versionsHost,
      ),
      el(
        "section",
        { class: "admin-section" },
        el("h2", { class: "admin-section-ttl" }, "MAPS"),
        el("p", { class: "admin-section-desc" },
          el("a", {
            href: "#builder",
            class: "admin-link",
          }, "Open Map Builder →"),
          " — create or edit maps in the browser.",
        ),
        mapsHost,
      ),
      el(
        "section",
        { class: "admin-section" },
        el("h2", { class: "admin-section-ttl" }, "RANDOM MAP POOL"),
        el("p", { class: "admin-section-desc" },
          "Maps the Skynet room (and any Random room) can roll between matches. " +
          "Untick a map to keep it out of the rotation — handy for sprawling maps " +
          "that play poorly with few players."),
        randomMapsHost,
      ),
      el(
        "section",
        { class: "admin-section" },
        el("h2", { class: "admin-section-ttl" }, "TURBO MODE"),
        el("p", { class: "admin-section-desc" },
          "When OFF, the web lobby hides the TURBO World preset and the STYLE " +
          "dropdown on game creation. Existing TURBO rooms continue to run; " +
          "this gate only controls web-side discovery."),
        turboHost,
      ),
      el(
        "section",
        { class: "admin-section" },
        el("h2", { class: "admin-section-ttl" }, "MATCH LOBBY WARMUP"),
        el("p", { class: "admin-section-desc" },
          "Countdown duration (seconds) for the pre-match Match Lobby on queued " +
          "FREE FOR ALL / TEAMS rooms. Players join, chat, pick a colour, and " +
          "optionally enter battle early during this window. Set to 0 to skip the " +
          "warmup and drop straight into the live match. Range 0–" + String(MAX_WARMUP_SECONDS) + "."),
        warmupHost,
      ),
      el(
        "section",
        { class: "admin-section" },
        el("h2", { class: "admin-section-ttl" }, "MIN LOBBY TIME"),
        el("p", { class: "admin-section-desc" },
          "Minimum seconds a fresh joiner is guaranteed in the Match Lobby. If they " +
          "join a room whose warmup has less time left, the countdown is extended " +
          "to give them at least this much time to tweak their tank. Set to 0 to " +
          "disable the floor. Range 0–" + String(MAX_MIN_LOBBY_SECONDS) + "."),
        minLobbyHost,
      ),
      el(
        "section",
        { class: "admin-section" },
        el("h2", { class: "admin-section-ttl" }, "SIEGE MODE"),
        el("p", { class: "admin-section-desc" },
          "When OFF, the web lobby hides the SIEGE mode option, the always-on " +
          "single-player challenge row, and any siege rooms. For testing."),
        siegeHost,
      ),
      el(
        "section",
        { class: "admin-section" },
        el("h2", { class: "admin-section-ttl" }, "SIEGE MAP POOL"),
        el("p", { class: "admin-section-desc" },
          "Maps the SIEGE challenge (and any random siege room) can roll " +
          "between runs. Untick a map to keep it out of the siege rotation."),
        siegeMapsHost,
      ),
      el(
        "section",
        { class: "admin-section" },
        el("h2", { class: "admin-section-ttl" }, "BATTLE ROYALE"),
        el("p", { class: "admin-section-desc" },
          "When OFF, the web lobby hides the BATTLE ROYALE tile and the matchmaker " +
          "refuses the queue. For testing."),
        brHost,
      ),
      el(
        "section",
        { class: "admin-section" },
        el("h2", { class: "admin-section-ttl" }, "BATTLE ROYALE MAP POOL"),
        el("p", { class: "admin-section-desc" },
          "Maps a BATTLE ROYALE match can roll. Each match randomly picks one. " +
          "Untick a map to keep it out of the battle-royale rotation."),
        brMapsHost,
      ),
    ),
  );

  playerHost.replaceChildren(buildPlayerPanel());
  void loadVersions(versionsHost);
  void loadMaps(mapsHost);
  void loadRandomMaps(randomMapsHost);
  void loadTurbo(turboHost);
  void loadWarmup(warmupHost);
  void loadMinLobby(minLobbyHost);
  void loadSiege(siegeHost);
  void loadSiegeMaps(siegeMapsHost);
  void loadBattleRoyale(brHost);
  void loadBattleRoyaleMaps(brMapsHost);

  return wrap;
}

function buildPlayerPanel(): HTMLElement {
  const input = el("input", {
    type: "text",
    class: "admin-input",
    placeholder: "username or email",
    maxlength: 254,
  }) as HTMLInputElement;
  const searchBtn = el("button", { type: "button", class: "admin-btn admin-btn-primary" }, "SEARCH");
  const status = el("div", { class: "admin-status" });
  const results = el("div", { class: "admin-maps-list" });

  async function runSearch(): Promise<void> {
    const q = input.value.trim();
    if (q.length < 1) {
      status.textContent = "Enter a username or email to search.";
      status.className = "admin-status admin-status-err";
      return;
    }
    searchBtn.setAttribute("disabled", "");
    status.textContent = "Searching…";
    status.className = "admin-status";
    results.replaceChildren();
    try {
      const { players } = await api.adminSearchPlayers(q);
      status.textContent = players.length === 0 ? "No players matched." : `${players.length} result(s).`;
      results.replaceChildren(...players.map((p) => buildPlayerRow(p)));
    } catch (e) {
      status.textContent = errorText(e, "Search failed.");
      status.classList.add("admin-status-err");
    } finally {
      searchBtn.removeAttribute("disabled");
    }
  }

  searchBtn.addEventListener("click", () => void runSearch());
  input.addEventListener("keydown", (e) => { if (e.key === "Enter") void runSearch(); });

  return el("div", {},
    el("div", { class: "admin-upload-row" }, input, searchBtn, status),
    results,
  );
}

function buildPlayerRow(p: AdminPlayerSummary): HTMLElement {
  const badge = el("span", { class: "admin-map-meta" }, p.premium ? "PREMIUM" : "free");
  const btn = el("button", { type: "button", class: "admin-btn" });
  const rowStatus = el("div", { class: "admin-status" });

  const render = (premium: boolean): void => {
    badge.textContent = premium ? "PREMIUM" : "free";
    btn.textContent = premium ? "REVOKE PREMIUM" : "GRANT PREMIUM";
    btn.className = premium ? "admin-btn admin-btn-danger" : "admin-btn admin-btn-primary";
  };
  render(p.premium);

  btn.addEventListener("click", async () => {
    const granting = btn.textContent === "GRANT PREMIUM";
    btn.setAttribute("disabled", "");
    rowStatus.textContent = granting ? "Granting…" : "Revoking…";
    rowStatus.className = "admin-status";
    try {
      const res = granting
        ? await api.adminGrantPremium(p.id)
        : await api.adminRevokePremium(p.id);
      render(res.premium);
      rowStatus.textContent = res.premium ? "Premium granted." : "Premium revoked.";
      rowStatus.classList.add("admin-status-ok");
    } catch (e) {
      rowStatus.textContent = errorText(e, "Action failed.");
      rowStatus.classList.add("admin-status-err");
    } finally {
      btn.removeAttribute("disabled");
    }
  });

  return el("div", { class: "admin-map-row" },
    el("div", { class: "admin-map-info" },
      el("span", { class: "admin-map-name" }, `${p.username}  `, badge),
      el("span", { class: "admin-map-meta" }, `#${p.id} · ${p.email}`),
    ),
    rowStatus,
    btn,
  );
}

async function loadWarmup(host: HTMLElement): Promise<void> {
  try {
    const { seconds } = await api.adminGetWarmup();
    host.replaceChildren(buildWarmupForm(seconds));
  } catch (e) {
    host.replaceChildren(el("div", { class: "admin-error" }, errorText(e, "Failed to load warmup config.")));
  }
}

function buildWarmupForm(initial: number): HTMLElement {
  const input = el("input", {
    type: "number",
    class: "admin-input",
    min: 0,
    max: MAX_WARMUP_SECONDS,
    step: 1,
    value: String(initial),
  }) as HTMLInputElement;
  const status = el("div", { class: "admin-status" });
  const save = el("button", { type: "button", class: "admin-btn admin-btn-primary" }, "SAVE");
  save.addEventListener("click", async () => {
    const n = Number.parseInt(input.value, 10);
    if (!Number.isFinite(n) || n < 0 || n > MAX_WARMUP_SECONDS) {
      status.textContent = `Enter a value between 0 and ${MAX_WARMUP_SECONDS}.`;
      status.className = "admin-status admin-status-err";
      return;
    }
    save.setAttribute("disabled", "");
    status.textContent = "Saving…";
    status.className = "admin-status";
    try {
      await api.adminUpdateWarmup({ seconds: n });
      status.textContent = "Saved.";
      status.classList.add("admin-status-ok");
    } catch (e) {
      status.textContent = errorText(e, "Save failed.");
      status.classList.add("admin-status-err");
    } finally {
      save.removeAttribute("disabled");
    }
  });
  return el("div", { class: "admin-form" },
    el("label", { class: "admin-row" },
      el("span", { class: "admin-row-lbl" }, "Warmup seconds"),
      input,
    ),
    el("div", { class: "admin-form-foot" }, save, status),
  );
}

async function loadMinLobby(host: HTMLElement): Promise<void> {
  try {
    const { seconds } = await api.adminGetMinLobby();
    host.replaceChildren(buildMinLobbyForm(seconds));
  } catch (e) {
    host.replaceChildren(el("div", { class: "admin-error" }, errorText(e, "Failed to load min-lobby config.")));
  }
}

function buildMinLobbyForm(initial: number): HTMLElement {
  const input = el("input", {
    type: "number",
    class: "admin-input",
    min: 0,
    max: MAX_MIN_LOBBY_SECONDS,
    step: 1,
    value: String(initial),
  }) as HTMLInputElement;
  const status = el("div", { class: "admin-status" });
  const save = el("button", { type: "button", class: "admin-btn admin-btn-primary" }, "SAVE");
  save.addEventListener("click", async () => {
    const n = Number.parseInt(input.value, 10);
    if (!Number.isFinite(n) || n < 0 || n > MAX_MIN_LOBBY_SECONDS) {
      status.textContent = `Enter a value between 0 and ${MAX_MIN_LOBBY_SECONDS}.`;
      status.className = "admin-status admin-status-err";
      return;
    }
    save.setAttribute("disabled", "");
    status.textContent = "Saving…";
    status.className = "admin-status";
    try {
      await api.adminUpdateMinLobby({ seconds: n });
      status.textContent = "Saved.";
      status.classList.add("admin-status-ok");
    } catch (e) {
      status.textContent = errorText(e, "Save failed.");
      status.classList.add("admin-status-err");
    } finally {
      save.removeAttribute("disabled");
    }
  });
  return el("div", { class: "admin-form" },
    el("label", { class: "admin-row" },
      el("span", { class: "admin-row-lbl" }, "Min lobby seconds"),
      input,
    ),
    el("div", { class: "admin-form-foot" }, save, status),
  );
}

async function loadSiege(host: HTMLElement): Promise<void> {
  try {
    const { enabled } = await api.adminGetSiege();
    host.replaceChildren(buildSiegeForm(enabled));
  } catch (e) {
    host.replaceChildren(el("div", { class: "admin-error" }, errorText(e, "Failed to load SIEGE config.")));
  }
}

function buildSiegeForm(initial: boolean): HTMLElement {
  const toggle = el("input", { type: "checkbox", class: "admin-check" }) as HTMLInputElement;
  toggle.checked = initial;
  const status = el("div", { class: "admin-status" });
  const save = el("button", { type: "button", class: "admin-btn admin-btn-primary" }, "SAVE");
  save.addEventListener("click", async () => {
    save.setAttribute("disabled", "");
    status.textContent = "Saving…";
    status.className = "admin-status";
    try {
      await api.adminUpdateSiege({ enabled: toggle.checked });
      status.textContent = "Saved.";
      status.classList.add("admin-status-ok");
    } catch (e) {
      status.textContent = errorText(e, "Save failed.");
      status.classList.add("admin-status-err");
    } finally {
      save.removeAttribute("disabled");
    }
  });
  return el("div", { class: "admin-form" },
    el("label", { class: "admin-row admin-check-row" },
      el("span", { class: "admin-row-lbl" }, "Enable SIEGE on Web"),
      toggle,
    ),
    el("div", { class: "admin-form-foot" }, save, status),
  );
}

async function loadSiegeMaps(host: HTMLElement): Promise<void> {
  try {
    const { maps } = await api.adminGetSiegeMaps();
    host.replaceChildren(buildSiegeMapsPanel(maps));
  } catch (e) {
    host.replaceChildren(el("div", { class: "admin-error" }, errorText(e, "Failed to load siege map pool.")));
  }
}

function buildSiegeMapsPanel(maps: AdminRandomMapEntry[]): HTMLElement {
  if (maps.length === 0) {
    return el("div", { class: "admin-empty" }, "No maps installed — upload maps above first.");
  }
  const checks: HTMLInputElement[] = [];
  const list = el("div", { class: "admin-maps-list" });
  for (const m of maps) {
    const cb = el("input", { type: "checkbox", class: "admin-check", "data-id": m.id }) as HTMLInputElement;
    cb.checked = m.enabled;
    checks.push(cb);
    list.append(
      el("label", { class: "admin-map-row admin-check-row" },
        el("div", { class: "admin-map-info" }, el("span", { class: "admin-map-name" }, m.name)),
        cb,
      ),
    );
  }
  const status = el("div", { class: "admin-status" });
  const save = el("button", { type: "button", class: "admin-btn admin-btn-primary" }, "SAVE POOL");
  save.addEventListener("click", async () => {
    const enabled = checks.filter((c) => c.checked).map((c) => c.getAttribute("data-id")!);
    if (enabled.length === 0) {
      status.textContent = "Pick at least one map for the siege pool.";
      status.className = "admin-status admin-status-err";
      return;
    }
    save.setAttribute("disabled", "");
    status.textContent = "Saving…";
    status.className = "admin-status";
    try {
      await api.adminUpdateSiegeMaps({ enabled });
      status.textContent = "Saved.";
      status.classList.add("admin-status-ok");
    } catch (e) {
      status.textContent = errorText(e, "Save failed.");
      status.classList.add("admin-status-err");
    } finally {
      save.removeAttribute("disabled");
    }
  });
  return el("div", {}, list, el("div", { class: "admin-form-foot" }, save, status));
}

async function loadBattleRoyale(host: HTMLElement): Promise<void> {
  try {
    const { enabled } = await api.adminGetBattleRoyale();
    host.replaceChildren(buildBattleRoyaleForm(enabled));
  } catch (e) {
    host.replaceChildren(el("div", { class: "admin-error" }, errorText(e, "Failed to load BATTLE ROYALE config.")));
  }
}

function buildBattleRoyaleForm(initial: boolean): HTMLElement {
  const toggle = el("input", { type: "checkbox", class: "admin-check" }) as HTMLInputElement;
  toggle.checked = initial;
  const status = el("div", { class: "admin-status" });
  const save = el("button", { type: "button", class: "admin-btn admin-btn-primary" }, "SAVE");
  save.addEventListener("click", async () => {
    save.setAttribute("disabled", "");
    status.textContent = "Saving…";
    status.className = "admin-status";
    try {
      await api.adminUpdateBattleRoyale({ enabled: toggle.checked });
      status.textContent = "Saved.";
      status.classList.add("admin-status-ok");
    } catch (e) {
      status.textContent = errorText(e, "Save failed.");
      status.classList.add("admin-status-err");
    } finally {
      save.removeAttribute("disabled");
    }
  });
  return el("div", { class: "admin-form" },
    el("label", { class: "admin-row admin-check-row" },
      el("span", { class: "admin-row-lbl" }, "Enable BATTLE ROYALE on Web"),
      toggle,
    ),
    el("div", { class: "admin-form-foot" }, save, status),
  );
}

async function loadBattleRoyaleMaps(host: HTMLElement): Promise<void> {
  try {
    const { maps } = await api.adminGetBattleRoyaleMaps();
    host.replaceChildren(buildBattleRoyaleMapsPanel(maps));
  } catch (e) {
    host.replaceChildren(el("div", { class: "admin-error" }, errorText(e, "Failed to load battle-royale map pool.")));
  }
}

function buildBattleRoyaleMapsPanel(maps: AdminRandomMapEntry[]): HTMLElement {
  if (maps.length === 0) {
    return el("div", { class: "admin-empty" }, "No maps installed — upload maps above first.");
  }
  const checks: HTMLInputElement[] = [];
  const list = el("div", { class: "admin-maps-list" });
  for (const m of maps) {
    const cb = el("input", { type: "checkbox", class: "admin-check", "data-id": m.id }) as HTMLInputElement;
    cb.checked = m.enabled;
    checks.push(cb);
    list.append(
      el("label", { class: "admin-map-row admin-check-row" },
        el("div", { class: "admin-map-info" }, el("span", { class: "admin-map-name" }, m.name)),
        cb,
      ),
    );
  }
  const status = el("div", { class: "admin-status" });
  const save = el("button", { type: "button", class: "admin-btn admin-btn-primary" }, "SAVE POOL");
  save.addEventListener("click", async () => {
    const enabled = checks.filter((c) => c.checked).map((c) => c.getAttribute("data-id")!);
    if (enabled.length === 0) {
      status.textContent = "Pick at least one map for the battle-royale pool.";
      status.className = "admin-status admin-status-err";
      return;
    }
    save.setAttribute("disabled", "");
    status.textContent = "Saving…";
    status.className = "admin-status";
    try {
      await api.adminUpdateBattleRoyaleMaps({ enabled });
      status.textContent = "Saved.";
      status.classList.add("admin-status-ok");
    } catch (e) {
      status.textContent = errorText(e, "Save failed.");
      status.classList.add("admin-status-err");
    } finally {
      save.removeAttribute("disabled");
    }
  });
  return el("div", {}, list, el("div", { class: "admin-form-foot" }, save, status));
}

async function loadTurbo(host: HTMLElement): Promise<void> {
  try {
    const { enabled } = await api.adminGetTurbo();
    host.replaceChildren(buildTurboForm(enabled));
  } catch (e) {
    host.replaceChildren(el("div", { class: "admin-error" }, errorText(e, "Failed to load TURBO config.")));
  }
}

function buildTurboForm(initial: boolean): HTMLElement {
  const toggle = el("input", { type: "checkbox", class: "admin-check" }) as HTMLInputElement;
  toggle.checked = initial;
  const status = el("div", { class: "admin-status" });
  const save = el("button", { type: "button", class: "admin-btn admin-btn-primary" }, "SAVE");
  save.addEventListener("click", async () => {
    save.setAttribute("disabled", "");
    status.textContent = "Saving…";
    status.className = "admin-status";
    try {
      await api.adminUpdateTurbo({ enabled: toggle.checked });
      status.textContent = "Saved.";
      status.classList.add("admin-status-ok");
    } catch (e) {
      status.textContent = errorText(e, "Save failed.");
      status.classList.add("admin-status-err");
    } finally {
      save.removeAttribute("disabled");
    }
  });
  return el("div", { class: "admin-form" },
    el("label", { class: "admin-row admin-check-row" },
      el("span", { class: "admin-row-lbl" }, "Enable TURBO on Web"),
      toggle,
    ),
    el("div", { class: "admin-form-foot" }, save, status),
  );
}

async function loadVersions(host: HTMLElement): Promise<void> {
  try {
    const { versions } = await api.adminGetVersions();
    host.replaceChildren(buildVersionForm(versions, host));
  } catch (e) {
    host.replaceChildren(el("div", { class: "admin-error" }, errorText(e, "Failed to load versions.")));
  }
}

function buildVersionForm(initial: AppVersions, host: HTMLElement): HTMLElement {
  const webClient = el("input", { type: "text", class: "admin-input", value: initial.webClient, maxlength: 32 }) as HTMLInputElement;
  const server    = el("input", { type: "text", class: "admin-input", value: initial.server,    maxlength: 32 }) as HTMLInputElement;
  const desktop   = el("input", { type: "text", class: "admin-input", value: initial.desktop,   maxlength: 32 }) as HTMLInputElement;
  const status    = el("div", { class: "admin-status" });

  const save = el("button", { type: "button", class: "admin-btn admin-btn-primary" }, "SAVE VERSIONS");
  save.addEventListener("click", async () => {
    save.setAttribute("disabled", "");
    status.textContent = "Saving…";
    status.className = "admin-status";
    try {
      await api.adminUpdateVersions({
        webClient: webClient.value.trim(),
        server:    server.value.trim(),
        desktop:   desktop.value.trim(),
      });
      status.textContent = "Saved.";
      status.classList.add("admin-status-ok");
    } catch (e) {
      status.textContent = errorText(e, "Save failed.");
      status.classList.add("admin-status-err");
    } finally {
      save.removeAttribute("disabled");
    }
  });

  return el("div", { class: "admin-form" },
    versionRow("Web Client", webClient),
    versionRow("Server",     server),
    versionRow("Desktop",    desktop),
    el("div", { class: "admin-form-foot" }, save, status),
  );

  // host param kept for symmetry with maps; unused once form is built.
  void host;
}

function versionRow(label: string, input: HTMLInputElement): HTMLElement {
  return el("label", { class: "admin-row" },
    el("span", { class: "admin-row-lbl" }, label),
    input,
  );
}

async function loadMaps(host: HTMLElement): Promise<void> {
  try {
    const { maps } = await api.adminListMaps();
    host.replaceChildren(buildMapsPanel(maps, host));
  } catch (e) {
    host.replaceChildren(el("div", { class: "admin-error" }, errorText(e, "Failed to load maps.")));
  }
}

function buildMapsPanel(maps: AdminMapEntry[], host: HTMLElement): HTMLElement {
  const fileInput = el("input", { type: "file", accept: ".map", class: "admin-file" }) as HTMLInputElement;
  const uploadBtn = el("button", { type: "button", class: "admin-btn admin-btn-primary" }, "UPLOAD MAP");
  const uploadStatus = el("div", { class: "admin-status" });

  uploadBtn.addEventListener("click", async () => {
    const file = fileInput.files?.[0];
    if (!file) {
      uploadStatus.textContent = "Pick a .map file first.";
      uploadStatus.className = "admin-status admin-status-err";
      return;
    }
    if (!/\.map$/i.test(file.name)) {
      uploadStatus.textContent = "Filename must end in .map";
      uploadStatus.className = "admin-status admin-status-err";
      return;
    }
    uploadBtn.setAttribute("disabled", "");
    uploadStatus.textContent = "Uploading…";
    uploadStatus.className = "admin-status";
    try {
      const dataBase64 = await readFileAsBase64(file);
      await api.adminUploadMap({ filename: file.name, dataBase64 });
      uploadStatus.textContent = `Uploaded ${file.name}.`;
      uploadStatus.classList.add("admin-status-ok");
      fileInput.value = "";
      await loadMaps(host); // refresh list
    } catch (e) {
      uploadStatus.textContent = errorText(e, "Upload failed.");
      uploadStatus.classList.add("admin-status-err");
    } finally {
      uploadBtn.removeAttribute("disabled");
    }
  });

  const list = el("div", { class: "admin-maps-list" });
  if (maps.length === 0) {
    list.append(el("div", { class: "admin-empty" }, "No maps installed."));
  } else {
    for (const m of maps) list.append(buildMapRow(m, host));
  }

  return el("div", {},
    el("div", { class: "admin-upload-row" }, fileInput, uploadBtn, uploadStatus),
    list,
  );
}

function buildMapRow(m: AdminMapEntry, host: HTMLElement): HTMLElement {
  const dlBtn = el("button", { type: "button", class: "admin-btn" }, "DOWNLOAD");
  dlBtn.addEventListener("click", async () => {
    dlBtn.setAttribute("disabled", "");
    try {
      const { dataBase64 } = await api.adminGetMapRaw(m.id);
      // Decode the base64 .map bytes and hand them to the browser as a file
      // download named after the map's on-disk filename.
      const bin = atob(dataBase64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/octet-stream" }));
      const a = el("a", { href: url, download: m.filename }) as HTMLAnchorElement;
      document.body.append(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(errorText(e, "Download failed."));
    } finally {
      dlBtn.removeAttribute("disabled");
    }
  });

  const delBtn = el("button", { type: "button", class: "admin-btn admin-btn-danger" }, "DELETE");
  delBtn.addEventListener("click", async () => {
    if (!confirm(`Delete map "${m.name}"? This cannot be undone.`)) return;
    delBtn.setAttribute("disabled", "");
    try {
      await api.adminDeleteMap(m.id);
      await loadMaps(host);
    } catch (e) {
      alert(errorText(e, "Delete failed."));
      delBtn.removeAttribute("disabled");
    }
  });
  return el("div", { class: "admin-map-row" },
    el("div", { class: "admin-map-info" },
      el("span", { class: "admin-map-name" }, m.name),
      el("span", { class: "admin-map-meta" }, `${m.filename} · ${formatSize(m.size)}`),
    ),
    dlBtn,
    delBtn,
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

async function loadRandomMaps(host: HTMLElement): Promise<void> {
  try {
    const { maps } = await api.adminGetRandomMaps();
    host.replaceChildren(buildRandomMapsPanel(maps));
  } catch (e) {
    host.replaceChildren(el("div", { class: "admin-error" }, errorText(e, "Failed to load random map pool.")));
  }
}

function buildRandomMapsPanel(maps: AdminRandomMapEntry[]): HTMLElement {
  if (maps.length === 0) {
    return el("div", { class: "admin-empty" }, "No maps installed — upload maps above first.");
  }

  const checks: HTMLInputElement[] = [];
  const list = el("div", { class: "admin-maps-list" });
  for (const m of maps) {
    const cb = el("input", { type: "checkbox", class: "admin-check", "data-id": m.id }) as HTMLInputElement;
    cb.checked = m.enabled;
    checks.push(cb);
    list.append(
      el("label", { class: "admin-map-row admin-check-row" },
        el("div", { class: "admin-map-info" }, el("span", { class: "admin-map-name" }, m.name)),
        cb,
      ),
    );
  }

  const status = el("div", { class: "admin-status" });
  const save = el("button", { type: "button", class: "admin-btn admin-btn-primary" }, "SAVE POOL");
  save.addEventListener("click", async () => {
    const enabled = checks.filter((c) => c.checked).map((c) => c.getAttribute("data-id")!);
    if (enabled.length === 0) {
      status.textContent = "Pick at least one map for the random pool.";
      status.className = "admin-status admin-status-err";
      return;
    }
    save.setAttribute("disabled", "");
    status.textContent = "Saving…";
    status.className = "admin-status";
    try {
      await api.adminUpdateRandomMaps({ enabled });
      status.textContent = "Saved.";
      status.classList.add("admin-status-ok");
    } catch (e) {
      status.textContent = errorText(e, "Save failed.");
      status.classList.add("admin-status-err");
    } finally {
      save.removeAttribute("disabled");
    }
  });

  return el("div", {},
    list,
    el("div", { class: "admin-form-foot" }, save, status),
  );
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("read_failed"));
        return;
      }
      // FileReader.readAsDataURL returns "data:<mime>;base64,<payload>".
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("read_failed"));
    reader.readAsDataURL(file);
  });
}

function errorText(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    const code = typeof e.body === "object" && e.body && "error" in e.body
      ? String((e.body as { error: unknown }).error)
      : "";
    switch (code) {
      case "map_exists":      return "A map with that name already exists.";
      case "invalid_map":     return "File is not a valid WinBolo .map.";
      case "invalid_input":   return "Invalid input.";
      case "invalid_size":    return "File is empty or too large.";
      case "not_authenticated": return "Session expired — log in again.";
      case "forbidden":       return "Admin access only.";
      default: return code || fallback;
    }
  }
  return fallback;
}
